import React, { useState, useMemo, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Plus,
  Edit,
  Trash2,
  Users,
  FileText,
  Calendar,
  Search,
  Filter,
  MoreVertical,
  UserPlus
} from 'lucide-react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Modal from '../../components/ui/Modal';
import { useToast } from '../../hooks/useToast';
import ToastContainer from '../../components/ui/ToastContainer';
import { apiService } from '../../services/api';
import { settingsService } from '../../services/api/settings';
import '../../styles/background-effects.css';


interface TeamFormData {
  name: string;
  description: string;
  members: number[];
}

const TeamsPage: React.FC = () => {
  const { toasts, showSuccess, showError, removeToast } = useToast();
  const [teams, setTeams] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showNewTeamModal, setShowNewTeamModal] = useState(false);
  const [showEditTeamModal, setShowEditTeamModal] = useState(false);
  const [editingTeam, setEditingTeam] = useState<any>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Charger les équipes et utilisateurs depuis l'API
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        console.log('👥 Chargement des équipes et utilisateurs depuis le backend...');
        
        const [teamsData, usersData] = await Promise.all([
          apiService.getTeams(),
          settingsService.getAllUsers()
        ]);
        
        console.log('✅ Équipes chargées:', teamsData.length);
        console.log('✅ Utilisateurs chargés:', usersData.length);
        
        // Formater les équipes pour l'UI
        const formattedTeams = teamsData.map((team: any) => ({
          id: team.id.toString(),
          name: team.name,
          description: team.description || '',
          members: team.members || [],
          members_count: team.members_count || 0,
          created_by_name: team.created_by_name || 'Inconnu',
          createdAt: team.created_at,
          is_active: team.is_active !== false
        }));
        
        // Formater les utilisateurs pour l'UI
        const formattedUsers = usersData.map((user: any) => ({
          id: user.id.toString(),
          name: user.full_name || `${user.first_name} ${user.last_name}`,
          email: user.email,
          role: user.role || 'user',
          department: user.department_name || 'Non assigné'
        }));
        
        setTeams(formattedTeams);
        setUsers(formattedUsers);
      } catch (err) {
        console.error('Erreur lors du chargement:', err);
        showError('Erreur de chargement', 'Impossible de charger les données');
        setTeams([]);
        setUsers([]);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [showError]);

  // Filtrage des équipes
  const filteredTeams = useMemo(() => {
    return teams.filter(team => {
      const matchesQuery = !searchQuery || 
        team.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        team.description.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesQuery;
    });
  }, [searchQuery, teams]);

  const handleNewTeam = async (data: TeamFormData) => {
    try {
      console.log('➕ Création d\'une nouvelle équipe:', data);
      const newTeam = await apiService.createTeam(data);
      
      // Reformater pour l'UI
      const formattedTeam = {
        id: newTeam.id.toString(),
        name: newTeam.name,
        description: newTeam.description || '',
        members: newTeam.members || [],
        members_count: newTeam.members_count || 0,
        created_by_name: newTeam.created_by_name || 'Inconnu',
        createdAt: newTeam.created_at,
        is_active: newTeam.is_active !== false
      };
      
      setTeams(prev => [formattedTeam, ...prev]);
      setShowNewTeamModal(false);
      showSuccess('Équipe créée avec succès', `${data.name} a été ajoutée à vos équipes`);
    } catch (err) {
      console.error('❌ Erreur création équipe:', err);
      showError('Erreur lors de la création', 'Impossible de créer l\'équipe. Veuillez réessayer.');
    }
  };

  const handleEditTeam = async (data: TeamFormData) => {
    if (!editingTeam || !editingTeam.id) {
      console.error('❌ editingTeam invalide:', editingTeam);
      showError('Erreur', 'Équipe invalide');
      return;
    }

    try {
      console.log('✏️ Modification de l\'équipe:', editingTeam.id, data);
      const updatedTeam = await apiService.updateTeam(editingTeam.id, data);
      
      // Reformater pour l'UI
      const formattedTeam = {
        id: updatedTeam.id.toString(),
        name: updatedTeam.name,
        description: updatedTeam.description || '',
        members: updatedTeam.members || [],
        members_count: updatedTeam.members_count || 0,
        created_by_name: updatedTeam.created_by_name || 'Inconnu',
        createdAt: updatedTeam.created_at,
        is_active: updatedTeam.is_active !== false
      };
      
      setTeams(prev => prev.map(t => t.id === editingTeam.id ? formattedTeam : t));
      setShowEditTeamModal(false);
      setEditingTeam(null);
      showSuccess('Équipe mise à jour', `${data.name} a été modifiée avec succès`);
    } catch (err) {
      console.error('❌ Erreur modification équipe:', err);
      showError('Erreur lors de la modification', 'Impossible de modifier l\'équipe. Veuillez réessayer.');
    }
  };

  const handleDeleteTeam = async (teamId: string) => {
    const team = teams.find(t => t.id === teamId);
    if (window.confirm(`Êtes-vous sûr de vouloir supprimer l'équipe "${team?.name}" ?`)) {
      try {
        console.log('🗑️ Suppression de l\'équipe:', teamId);
        await apiService.deleteTeam(teamId);
        setTeams(prev => prev.filter(t => t.id !== teamId));
        showSuccess('Équipe supprimée', `${team?.name} a été supprimée de vos équipes`);
      } catch (err) {
        console.error('❌ Erreur suppression équipe:', err);
        showError('Erreur lors de la suppression', 'Impossible de supprimer l\'équipe');
      }
    }
  };

  const getTeamDocuments = (teamId: string): any[] => {
    // Les documents seront chargés via l'API si besoin
    return [];
  };

  const getTeamProjects = (teamId: string): any[] => {
    // Les projets seront chargés via l'API si besoin
    return [];
  };

  const getTeamMembers = (memberIds: number[]): any[] => {
    if (!memberIds || !Array.isArray(memberIds)) return [];
    return users.filter((user: any) => memberIds.includes(parseInt(user.id)));
  };

  return (
    <DashboardLayout>
      {/* Aurora Background Effect */}
      
      <div className="space-y-6 content-above-aurora">
        {/* En-tête */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-eg-encre">Équipes</h1>
            <p className="text-eg-gris">
              {filteredTeams.length} équipe{filteredTeams.length !== 1 ? 's' : ''} trouvée{filteredTeams.length !== 1 ? 's' : ''}
            </p>
          </div>
          <Button 
            onClick={() => setShowNewTeamModal(true)}
            icon={<Plus className="w-4 h-4" />}
          >
            Nouvelle Équipe
          </Button>
        </div>

        {/* Barre de recherche */}
        <Card>
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1">
              <Input
                placeholder="Rechercher par nom, description..."
                value={searchQuery}
                onChange={setSearchQuery}
                icon={<Search className="w-5 h-5" />}
              />
            </div>
            <Button
              variant={showFilters ? 'primary' : 'outline'}
              onClick={() => setShowFilters(!showFilters)}
              icon={<Filter className="w-4 h-4" />}
            >
              Filtres
            </Button>
          </div>
        </Card>

        {/* Liste des équipes */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredTeams.map((team) => (
            <motion.div
              key={team.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <Card className="h-full">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-eg-voile rounded-md flex items-center justify-center">
                      <Users className="w-5 h-5 text-eg-accent" />
                    </div>
                    <h3 className="text-lg font-semibold text-eg-encre">{team.name}</h3>
                  </div>
                  <div className="relative">
                    <button
                      onClick={() => {
                        setEditingTeam(team);
                        setShowEditTeamModal(true);
                      }}
                      className="p-1 text-eg-estompe hover:text-eg-gris hover:bg-eg-voile rounded"
                    >
                      <MoreVertical className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <p className="text-eg-gris text-sm mb-4 line-clamp-2">
                  {team.description}
                </p>

                <div className="space-y-3">
                  {/* Statistiques */}
                  <div className="flex items-center justify-between text-sm text-eg-gris">
                    <div className="flex items-center">
                      <FileText className="w-4 h-4 mr-1" />
                      {getTeamDocuments(team.id).length} documents
                    </div>
                    <div className="flex items-center">
                      <Users className="w-4 h-4 mr-1" />
                      {team.members.length} membres
                    </div>
                  </div>

                  {/* Membres */}
                  <div>
                    <p className="text-xs font-medium text-eg-gris mb-2">MEMBRES</p>
                    <div className="flex items-center space-x-1">
                      {getTeamMembers(team.members).slice(0, 4).map((member) => (
                        <div
                          key={member.id}
                          className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-xs font-medium text-eg-gris"
                          title={member.name}
                        >
                          {member.name.charAt(0)}
                        </div>
                      ))}
                      {team.members.length > 4 && (
                        <div className="w-8 h-8 rounded-full bg-eg-voile flex items-center justify-center text-xs text-eg-gris">
                          +{team.members.length - 4}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Projets associés */}
                  <div>
                    <p className="text-xs font-medium text-eg-gris mb-2">PROJETS ASSOCIÉS</p>
                    <div className="space-y-1">
                      {getTeamProjects(team.id).slice(0, 2).map((project) => (
                        <div key={project.id} className="flex items-center space-x-2">
                          <div 
                            className="w-2 h-2 rounded-full"
                            style={{ backgroundColor: project.color }}
                          ></div>
                          <span className="text-xs text-eg-gris">{project.name}</span>
                        </div>
                      ))}
                      {getTeamProjects(team.id).length > 2 && (
                        <p className="text-xs text-eg-estompe">
                          +{getTeamProjects(team.id).length - 2} autres projets
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Date de création */}
                  <div className="flex items-center text-xs text-eg-estompe">
                    <Calendar className="w-3 h-3 mr-1" />
                    Créée le {new Date(team.createdAt).toLocaleDateString()}
                  </div>

                  {/* Actions */}
                  <div className="flex space-x-2 pt-3 border-t border-eg-bordure">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setEditingTeam(team);
                        setShowEditTeamModal(true);
                      }}
                    >
                      <Edit className="w-3 h-3 mr-1" />
                      Modifier
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDeleteTeam(team.id)}
                      className="text-eg-accent hover:text-red-700"
                    >
                      <Trash2 className="w-3 h-3 mr-1" />
                      Supprimer
                    </Button>
                  </div>
                </div>
              </Card>
            </motion.div>
          ))}
        </div>

        {filteredTeams.length === 0 && (
          <Card className="text-center py-12">
            <div className="w-12 h-12 bg-eg-voile rounded-full flex items-center justify-center mx-auto mb-4">
              <Users className="w-6 h-6 text-eg-estompe" />
            </div>
            <h3 className="text-lg font-medium text-eg-encre mb-2">Aucune équipe trouvée</h3>
            <p className="text-eg-gris mb-4">Commencez par créer votre première équipe</p>
            <Button onClick={() => setShowNewTeamModal(true)}>
              Créer une équipe
            </Button>
          </Card>
        )}
      </div>

      {/* Modal Nouvelle Équipe */}
      <Modal
        isOpen={showNewTeamModal}
        onClose={() => setShowNewTeamModal(false)}
        title="Nouvelle Équipe"
        size="md"
      >
        <TeamForm
          onSubmit={handleNewTeam}
          onCancel={() => setShowNewTeamModal(false)}
          availableUsers={users}
        />
      </Modal>

      {/* Modal Éditer Équipe */}
      <Modal
        isOpen={showEditTeamModal}
        onClose={() => {
          setShowEditTeamModal(false);
          setEditingTeam(null);
        }}
        title="Modifier l'Équipe"
        size="md"
      >
        <TeamForm
          onSubmit={handleEditTeam}
          onCancel={() => {
            setShowEditTeamModal(false);
            setEditingTeam(null);
          }}
          initialData={editingTeam}
          availableUsers={users}
        />
      </Modal>

      {/* Notifications Toast */}
      <ToastContainer toasts={toasts} onRemoveToast={removeToast} />
    </DashboardLayout>
  );
};

// Composant formulaire pour les équipes
interface TeamFormProps {
  onSubmit: (data: TeamFormData) => void;
  onCancel: () => void;
  initialData?: any;
  availableUsers: any[];
}

const TeamForm: React.FC<TeamFormProps> = ({ onSubmit, onCancel, initialData, availableUsers }) => {
  const [formData, setFormData] = useState<TeamFormData>({
    name: initialData?.name || '',
    description: initialData?.description || '',
    members: initialData?.members || []
  });
  const [memberSearch, setMemberSearch] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  // Filtrer les utilisateurs basé sur la recherche
  const filteredUsers = availableUsers.filter((user: any) => 
    user.name.toLowerCase().includes(memberSearch.toLowerCase()) ||
    user.email.toLowerCase().includes(memberSearch.toLowerCase())
  );

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Nom de l'équipe */}
      <div>
        <label className="block text-sm font-medium text-eg-brun mb-2">
          Nom de l'équipe
        </label>
        <Input
          value={formData.name}
          onChange={(value) => setFormData(prev => ({ ...prev, name: value }))}
          placeholder="Nom de l'équipe"
          required
        />
      </div>

      {/* Description */}
      <div>
        <label className="block text-sm font-medium text-eg-brun mb-2">
          Description
        </label>
        <textarea
          value={formData.description}
          onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
          placeholder="Description de l'équipe..."
          className="w-full px-3 py-2 border border-[#DDE2DD] rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
          rows={3}
        />
      </div>

      {/* Membres */}
      <div>
        <label className="block text-sm font-medium text-eg-brun mb-2">
          <UserPlus className="w-4 h-4 inline mr-1" />
          Membres de l'équipe
        </label>
        
        {/* Barre de recherche */}
        <div className="mb-3">
          <Input
            placeholder="Rechercher par nom ou email..."
            value={memberSearch}
            onChange={setMemberSearch}
            icon={<Search className="w-4 h-4" />}
          />
        </div>

        <div className="space-y-2 max-h-48 overflow-y-auto border border-eg-bord rounded-md p-3">
          {filteredUsers.length > 0 ? (
            filteredUsers.map((user) => (
            <label key={user.id} className="flex items-center p-2 hover:bg-eg-papier rounded">
              <input
                type="checkbox"
                checked={formData.members.includes(parseInt(user.id))}
                onChange={(e) => {
                  const userId = parseInt(user.id);
                  if (e.target.checked) {
                    setFormData(prev => ({
                      ...prev,
                      members: [...prev.members, userId]
                    }));
                  } else {
                    setFormData(prev => ({
                      ...prev,
                      members: prev.members.filter((id: number) => id !== userId)
                    }));
                  }
                }}
                className="w-4 h-4 text-eg-accent border-[#DDE2DD] rounded focus:ring-primary-500"
              />
              <div className="ml-3 flex items-center">
                <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center text-xs font-medium text-eg-gris mr-2">
                  {user.name.charAt(0)}
                </div>
                <div>
                  <p className="text-sm font-medium text-eg-brun">{user.name}</p>
                  <p className="text-xs text-eg-gris">{user.email}</p>
                </div>
              </div>
            </label>
          ))
          ) : (
            <div className="text-center py-4 text-eg-gris">
              <Search className="w-6 h-6 mx-auto mb-2 text-gray-300" />
              <p className="text-sm">Aucun utilisateur trouvé</p>
              <p className="text-xs">Essayez de modifier votre recherche</p>
            </div>
          )}
        </div>
        <p className="text-xs text-eg-gris mt-1">
          {formData.members.length} membre{formData.members.length !== 1 ? 's' : ''} sélectionné{formData.members.length !== 1 ? 's' : ''}
        </p>
      </div>

      {/* Actions */}
      <div className="flex justify-end space-x-3 pt-4 border-t border-eg-bord">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
        >
          Annuler
        </Button>
        <Button
          type="submit"
        >
          {initialData ? 'Modifier l\'équipe' : 'Créer l\'équipe'}
        </Button>
      </div>
    </form>
  );
};

export default TeamsPage;
